import React, { useState, useRef } from 'react';
import MathText from './MathText';

const previewLeafSelector = 'mjx-mi, mjx-mn, mjx-mo, mjx-mtext';
const previewStructureSelector = 'mjx-frac, mjx-sqrt, mjx-root';

const latexSymbolMap = {
  '\\pi': 'π',
  '\\alpha': 'α',
  '\\beta': 'β',
  '\\gamma': 'γ',
  '\\delta': 'δ',
  '\\epsilon': 'ε',
  '\\zeta': 'ζ',
  '\\eta': 'η',
  '\\theta': 'θ',
  '\\lambda': 'λ',
  '\\mu': 'μ',
  '\\rho': 'ρ',
  '\\sigma': 'σ',
  '\\tau': 'τ',
  '\\phi': 'φ',
  '\\omega': 'ω',
  '\\Gamma': 'Γ',
  '\\Delta': 'Δ',
  '\\Theta': 'Θ',
  '\\Lambda': 'Λ',
  '\\Pi': 'Π',
  '\\Sigma': 'Σ',
  '\\Phi': 'Φ',
  '\\Psi': 'Ψ',
  '\\Omega': 'Ω',
  '\\int': '∫',
  '\\iint': '∬',
  '\\oint': '∮',
  '\\pm': '±',
  '\\mp': '∓',
  '\\times': '×',
  '\\cdot': '⋅',
  '\\div': '÷',
  '\\approx': '≈',
  '\\neq': '≠',
  '\\le': '≤',
  '\\ge': '≥',
  '\\equiv': '≡',
  '\\propto': '∝',
  '\\forall': '∀',
  '\\exists': '∃',
  '\\in': '∈',
  '\\notin': '∉',
  '\\subset': '⊂',
  '\\cup': '∪',
  '\\cap': '∩',
  '\\emptyset': '∅',
  '\\setminus': '∖',
  '\\Rightarrow': '⇒',
  '\\Leftrightarrow': '⇔',
  '\\to': '→',
  '\\infty': '∞',
  '\\partial': '∂',
  '\\nabla': '∇',
  '\\sum': '∑',
  '\\prod': '∏',
  '\\circ': '°',
  '\\therefore': '∴',
  '\\because': '∵',
  '\\land': '∧',
  '\\lor': '∨',
  '\\neg': '¬',
};

const nonLeafLatexCommands = new Set([
  '\\frac',
  '\\sqrt',
  '\\begin',
  '\\end',
  '\\left',
  '\\right',
  '\\mathbf',
  '\\mathbb',
  '\\mathcal',
  '\\mathfrak',
  '\\mathsf',
  '\\text',
  '\\hline',
  '\\underbrace',
  '\\overbrace',
  '\\vec',
  '\\overline',
  '\\enclose',
]);

const normalizePreviewText = (text) => (
  String(text || '')
    .trim()
    .replace(/\u2212/g, '-')
    .replace(/\u2062/g, '')
    .replace(/\s+/g, ' ')
);

const findClosingBrace = (latex, openIndex) => {
  let depth = 0;
  for (let i = openIndex; i < latex.length; i++) {
    if (latex[i] === '\\') {
      i++;
    } else if (latex[i] === '{') {
      depth++;
    } else if (latex[i] === '}') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
};

const findCommandBlock = (latex, command, startIndex) => {
  if (!latex.startsWith(command, startIndex)) return null;

  let end = startIndex + command.length;
  while (latex[end] === ' ') end++;

  if (latex[end] === '[') {
    const closeOptional = latex.indexOf(']', end + 1);
    if (closeOptional !== -1) end = closeOptional + 1;
  }

  if (command === '\\frac') {
    for (let group = 0; group < 2; group++) {
      while (latex[end] === ' ') end++;
      if (latex[end] !== '{') break;
      const close = findClosingBrace(latex, end);
      if (close === -1) break;
      end = close + 1;
    }
  } else if (latex[end] === '{') {
    const close = findClosingBrace(latex, end);
    if (close !== -1) end = close + 1;
  }

  return { pos: startIndex, len: Math.max(command.length, end - startIndex) };
};

const findRequiredGroupEnd = (latex, startIndex) => {
  let i = startIndex;
  while (latex[i] === ' ') i++;
  if (latex[i] !== '{') return startIndex;

  const close = findClosingBrace(latex, i);
  return close === -1 ? startIndex : close + 1;
};

const mathDelimiterPairs = [
  { open: '\\[', close: '\\]' },
  { open: '\\(', close: '\\)' },
  { open: '$$', close: '$$' },
  { open: '$', close: '$' },
];

const findWrappingMathDelimiter = (latex) => {
  const rawLatex = latex || '';
  const trimmedLatex = rawLatex.trim();
  const leadingOffset = trimmedLatex ? rawLatex.indexOf(trimmedLatex) : 0;
  const pair = mathDelimiterPairs.find(({ open, close }) => (
    trimmedLatex.startsWith(open) &&
    trimmedLatex.endsWith(close) &&
    trimmedLatex.length >= open.length + close.length
  ));

  if (!pair) return null;

  return {
    ...pair,
    leadingOffset,
    innerStart: leadingOffset + pair.open.length,
    innerEnd: leadingOffset + trimmedLatex.length - pair.close.length,
  };
};

const stripWrappingMathDelimiter = (latex) => {
  const wrapper = findWrappingMathDelimiter(latex);
  if (!wrapper) return latex;
  return latex.slice(wrapper.innerStart, wrapper.innerEnd).trim();
};

const sourceClassFor = (pos, len) => `latex-src-${pos}-${pos + len}`;

const wrapSelectableLatex = (latex, pos, len, sourceOffset = 0) => (
  `\\class{${sourceClassFor(sourceOffset + pos, len)}}{${latex.slice(pos, pos + len)}}`
);

const annotateLatexForSelection = (latex, sourceOffset = 0) => {
  let output = '';
  let i = 0;

  while (i < latex.length) {
    const char = latex[i];

    if (char === '\\') {
      const match = latex.slice(i).match(/^\\[a-zA-Z]+|^\\./);
      if (match) {
        const raw = match[0];

        if (raw === '\\\\') {
          output += raw;
          i += raw.length;
          continue;
        }

        if (raw === '\\begin' || raw === '\\end') {
          const end = findRequiredGroupEnd(latex, i + raw.length);
          output += latex.slice(i, end);
          i = end;
          continue;
        }

        if (raw === '\\left' || raw === '\\right') {
          output += raw;
          i += raw.length;
          while (latex[i] === ' ') {
            output += latex[i];
            i++;
          }
          if (latex[i]) {
            output += latex[i];
            i++;
          }
          continue;
        }

        if (nonLeafLatexCommands.has(raw)) {
          output += raw;
        } else if (/^\\[,;:! ]$/.test(raw) || raw === '\\quad' || raw === '\\qquad') {
          output += raw;
        } else {
          output += wrapSelectableLatex(latex, i, raw.length, sourceOffset);
        }
        i += raw.length;
        continue;
      }
    }

    if (/[0-9]/.test(char)) {
      const match = latex.slice(i).match(/^[0-9]+(?:\.[0-9]+)?/);
      output += wrapSelectableLatex(latex, i, match[0].length, sourceOffset);
      i += match[0].length;
      continue;
    }

    if (/[a-zA-Z]/.test(char)) {
      output += wrapSelectableLatex(latex, i, 1, sourceOffset);
      i++;
      continue;
    }

    output += char;
    i++;
  }

  return output;
};

const buildPreviewMathText = (latex, mathMode) => {
  const rawLatex = latex || ' ';
  const existingPair = findWrappingMathDelimiter(rawLatex);

  if (existingPair) {
    const innerLatex = rawLatex.slice(existingPair.innerStart, existingPair.innerEnd) || ' ';

    return `${existingPair.open}\\require{html}${annotateLatexForSelection(innerLatex, existingPair.innerStart)}${existingPair.close}`;
  }

  const previewLatex = `\\require{html}${annotateLatexForSelection(rawLatex)}`;
  return mathMode === 'display' ? `$$${previewLatex}$$` : `$${previewLatex}$`;
};

const buildLatexPreviewTokens = (latex) => {
  const tokens = [];
  let i = 0;

  while (i < latex.length) {
    const char = latex[i];

    if (/\s/.test(char) || '{}[]^_&'.includes(char)) {
      i++;
      continue;
    }

    if (char === '\\') {
      const match = latex.slice(i).match(/^\\[a-zA-Z]+|^\\./);
      if (match) {
        const raw = match[0];
        if (raw === '\\\\') {
          i += raw.length;
          continue;
        }

        if (raw === '\\begin' || raw === '\\end') {
          i = findRequiredGroupEnd(latex, i + raw.length);
          continue;
        }

        if (raw === '\\left' || raw === '\\right') {
          i += raw.length;
          while (latex[i] === ' ') i++;
          if (latex[i]) i++;
          continue;
        }

        if (!nonLeafLatexCommands.has(raw)) {
          const display = latexSymbolMap[raw] || raw.slice(1);
          tokens.push({ display: normalizePreviewText(display), pos: i, len: raw.length });
        }
        i += raw.length;
        continue;
      }
    }

    if (/[0-9]/.test(char)) {
      const match = latex.slice(i).match(/^[0-9]+(?:\.[0-9]+)?/);
      tokens.push({ display: normalizePreviewText(match[0]), pos: i, len: match[0].length });
      i += match[0].length;
      continue;
    }

    if (/[a-zA-Z]/.test(char)) {
      tokens.push({ display: normalizePreviewText(char), pos: i, len: 1 });
      i++;
      continue;
    }

    tokens.push({ display: normalizePreviewText(char), pos: i, len: 1 });
    i++;
  }

  return tokens.filter(token => token.display);
};

const getPreviewLeaves = (container) => (
  Array.from(container.querySelectorAll(previewLeafSelector))
    .filter(el => !el.querySelector(previewLeafSelector))
);

const getRenderedPreviewTokens = (leaves) => (
  leaves
    .map(leaf => normalizePreviewText(leaf.textContent))
    .filter(Boolean)
);

const findAlignedSourceToken = (renderedTokens, sourceTokens, renderedIndex) => {
  if (renderedIndex < 0 || !renderedTokens[renderedIndex]) return null;

  const renderedCount = renderedTokens.length;
  const sourceCount = sourceTokens.length;
  const dp = Array.from({ length: renderedCount + 1 }, () => Array(sourceCount + 1).fill(0));

  for (let i = renderedCount - 1; i >= 0; i--) {
    for (let j = sourceCount - 1; j >= 0; j--) {
      if (renderedTokens[i] === sourceTokens[j].display) {
        dp[i][j] = 1 + dp[i + 1][j + 1];
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  let i = 0;
  let j = 0;
  while (i < renderedCount && j < sourceCount) {
    if (renderedTokens[i] === sourceTokens[j].display) {
      if (i === renderedIndex) return sourceTokens[j];
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      i++;
    } else {
      j++;
    }
  }

  return null;
};

const getSourceMatchFromClass = (element) => {
  const sourceElement = element?.closest?.('[class*="latex-src-"]');
  if (!sourceElement) return null;

  const className = typeof sourceElement.className === 'string'
    ? sourceElement.className
    : sourceElement.getAttribute('class') || '';
  const match = className.match(/(?:^|\s)latex-src-(\d+)-(\d+)(?:\s|$)/);
  if (!match) return null;

  const start = Number(match[1]);
  const end = Number(match[2]);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;

  return { pos: start, len: end - start };
};

const MathToolbar = ({ targetId }) => {
  const [showModal, setShowModal] = useState(false);
  const [tempText, setTempText] = useState('');
  const [activeCategory, setActiveCategory] = useState('Algebra');
  const popupInputRef = useRef(null);

  const [mathMode, setMathMode] = useState('inline');
  const previewMathText = buildPreviewMathText(tempText, mathMode);

  const stopModalEventPropagation = (e) => {
    e.stopPropagation();
  };

  const categories = {
    Delimiters: [
      { label: '( )', value: '()', title: 'Parentheses' },
      { label: '[ ]', value: '[]', title: 'Brackets' },
      { label: '\\( \\)', value: '\\(  \\)', title: 'Inline MathJax Delimiters' },
      { label: '\\[ \\]', value: '\\[  \\]', title: 'Display MathJax Delimiters' },
      { label: '$ $', value: '$  $', title: 'Inline Dollar Delimiters' },
      { label: '$$ $$', value: '$$  $$', title: 'Display Dollar Delimiters' },
      { label: '⎛ ⎞', value: '\\left(  \\right)', title: 'Auto-sizing Parentheses' },
      { label: '⎡ ⎤', value: '\\left[  \\right]', title: 'Auto-sizing Brackets' },
      { label: '⎧ ⎫', value: '\\left\\{  \\right\\}', title: 'Auto-sizing Braces' },
      { label: '⎧ .', value: '\\left\\{  \\right.', title: 'Left Brace Only' },
      { label: '. ⎫', value: '\\left.  \\right\\}', title: 'Right Brace Only' },
    ],
    Algebra: [
      { label: 'f/x', value: '\\frac{}{}', title: 'Fraction' },
      { label: '√', value: '\\sqrt{}', title: 'Square Root' },
      { label: '∛', value: '\\sqrt[3]{}', title: 'Cube Root' },
      { label: 'xⁿ', value: '^{}', title: 'Power' },
      { label: 'xₙ', value: '_{}', title: 'Subscript' },
      { label: 'log', value: '\\log_{}( )', title: 'Logarithm' },
      { label: 'ln', value: '\\ln( )', title: 'Natural Logarithm' },
      { label: '|x|', value: '| |', title: 'Absolute Value' },
      { label: 'n!', value: 'n!', title: 'Factorial' },
      { label: '⏟', value: '\\underbrace{a + b}_{text}', title: 'Underbrace' },
      { label: '⏞', value: '\\overbrace{a + b}^{text}', title: 'Overbrace' },
    ],
    Calculus: [
      { label: '∫', value: '\\int_{}^{}', title: 'Single Integral' },
      { label: '∬', value: '\\iint_{}^{}', title: 'Double Integral' },
      { label: '∮', value: '\\oint', title: 'Contour Integral' },
      { label: 'Σ', value: '\\sum_{}^{}', title: 'Summation' },
      { label: 'Π', value: '\\prod_{}^{}', title: 'Product' },
      { label: 'lim', value: '\\lim_{x \\to \\infty}', title: 'Limit' },
      { label: 'd/dx', value: '\\frac{d}{dx}', title: 'Derivative' },
      { label: '∂', value: '\\partial', title: 'Partial Derivative' },
      { label: '∇', value: '\\nabla', title: 'Gradient / Del' },
      { label: '∞', value: '\\infty', title: 'Infinity' },
    ],
    Trig: [
      { label: 'sin', value: '\\sin( )', title: 'Sine' },
      { label: 'cos', value: '\\cos( )', title: 'Cosine' },
      { label: 'tan', value: '\\tan( )', title: 'Tangent' },
      { label: 'sec', value: '\\sec( )', title: 'Secant' },
      { label: 'csc', value: '\\csc( )', title: 'Cosecant' },
      { label: 'cot', value: '\\cot( )', title: 'Cotangent' },
      { label: 'sin⁻¹', value: '\\sin^{-1}( )', title: 'Arcsine' },
      { label: 'cos⁻¹', value: '\\cos^{-1}( )', title: 'Arccosine' },
      { label: 'tan⁻¹', value: '\\tan^{-1}( )', title: 'Arctangent' },
      { label: '°', value: '^{\\circ}', title: 'Degrees' },
    ],
    Matrices: [
      { label: '(2x2)', value: '\\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix}', title: '2x2 Matrix (Parentheses)' },
      { label: '[2x2]', value: '\\begin{bmatrix} a & b \\\\ c & d \\end{bmatrix}', title: '2x2 Matrix (Brackets)' },
      { label: '|2x2|', value: '\\begin{vmatrix} a & b \\\\ c & d \\end{vmatrix}', title: '2x2 Determinant' },
      { label: '(3x3)', value: '\\begin{pmatrix} a & b & c \\\\ d & e & f \\\\ g & h & i \\end{pmatrix}', title: '3x3 Matrix (Parentheses)' },
      { label: '[3x3]', value: '\\begin{bmatrix} a & b & c \\\\ d & e & f \\\\ g & h & i \\end{bmatrix}', title: '3x3 Matrix (Brackets)' },
      { label: 'Cases', value: 'f(x) = \\begin{cases} x & x \\ge 0 \\\\ -x & x < 0 \\end{cases}', title: 'Piecewise / Cases' },
      { label: 'System', value: '\\begin{cases} x + y = 2 \\\\ x - y = 0 \\end{cases}', title: 'System of Equations' },
      { label: 'x⃗', value: '\\vec{}', title: 'Vector representation' },
      { label: 'AB̄', value: '\\overline{}', title: 'Line segment overline' },
    ],
    Tables: [
      { label: '2x2 grid', value: '\\begin{array}{|c|c|} \\hline a & b \\\\ \\hline c & d \\\\ \\hline \\end{array}', title: '2x2 Table with Borders' },
      { label: '3x3 grid', value: '\\begin{array}{|c|c|c|} \\hline a & b & c \\\\ \\hline d & e & f \\\\ \\hline g & h & i \\\\ \\hline \\end{array}', title: '3x3 Table with Borders' },
      { label: '2 col', value: '\\begin{array}{cc} a & b \\\\ c & d \\end{array}', title: '2 Column Array without Borders' },
      { label: '3 col', value: '\\begin{array}{ccc} a & b & c \\\\ d & e & f \\end{array}', title: '3 Column Array without Borders' },
      { label: 'cases tbl', value: '\\left\\{ \\begin{array}{ll} a & x > 0 \\\\ b & x \\le 0 \\end{array} \\right.', title: 'Piecewise Table with Left Brace' },
      { label: '\\hline', value: '\\hline ', title: 'Horizontal Table Line' },
      { label: '&', value: ' & ', title: 'Next Table Cell' },
      { label: '\\\\', value: ' \\\\ ', title: 'New Table Row' },
      { label: '|c|c|', value: '|c|c|', title: 'Two Centered Columns with Vertical Borders' },
    ],

    Greek: [
      { label: 'α', value: '\\alpha', title: 'alpha' },
      { label: 'β', value: '\\beta', title: 'beta' },
      { label: 'γ', value: '\\gamma', title: 'gamma' },
      { label: 'δ', value: '\\delta', title: 'delta' },
      { label: 'ε', value: '\\epsilon', title: 'epsilon' },
      { label: 'ζ', value: '\\zeta', title: 'zeta' },
      { label: 'η', value: '\\eta', title: 'eta' },
      { label: 'θ', value: '\\theta', title: 'theta' },
      { label: 'λ', value: '\\lambda', title: 'lambda' },
      { label: 'μ', value: '\\mu', title: 'mu' },
      { label: 'π', value: '\\pi', title: 'pi' },
      { label: 'ρ', value: '\\rho', title: 'rho' },
      { label: 'σ', value: '\\sigma', title: 'sigma' },
      { label: 'τ', value: '\\tau', title: 'tau' },
      { label: 'φ', value: '\\phi', title: 'phi' },
      { label: 'ω', value: '\\omega', title: 'omega' },
      { label: 'Γ', value: '\\Gamma', title: 'Gamma' },
      { label: 'Δ', value: '\\Delta', title: 'Delta' },
      { label: 'Θ', value: '\\Theta', title: 'Theta' },
      { label: 'Λ', value: '\\Lambda', title: 'Lambda' },
      { label: 'Π', value: '\\Pi', title: 'Pi' },
      { label: 'Σ', value: '\\Sigma', title: 'Sigma' },
      { label: 'Φ', value: '\\Phi', title: 'Phi' },
      { label: 'Ψ', value: '\\Psi', title: 'Psi' },
      { label: 'Ω', value: '\\Omega', title: 'Omega' },
    ],
    Symbols: [
      { label: '±', value: '\\pm', title: 'Plus-Minus' },
      { label: '∓', value: '\\mp', title: 'Minus-Plus' },
      { label: '×', value: '\\times', title: 'Multiply (Times)' },
      { label: '⋅', value: '\\cdot', title: 'Dot Multiply' },
      { label: '÷', value: '\\div', title: 'Divide' },
      { label: '≈', value: '\\approx', title: 'Approximately Equal' },
      { label: '≠', value: '\\neq', title: 'Not Equal' },
      { label: '≤', value: '\\le', title: 'Less Than or Equal' },
      { label: '≥', value: '\\ge', title: 'Greater Than or Equal' },
      { label: '≡', value: '\\equiv', title: 'Identically Equal' },
      { label: '∝', value: '\\propto', title: 'Proportional' },
      { label: '∀', value: '\\forall', title: 'For All' },
      { label: '∃', value: '\\exists', title: 'Exists' },
      { label: '∈', value: '\\in', title: 'Element Of' },
      { label: '∉', value: '\\notin', title: 'Not Element Of' },
      { label: '⊂', value: '\\subset', title: 'Subset Of' },
      { label: '∪', value: '\\cup', title: 'Union' },
      { label: '∩', value: '\\cap', title: 'Intersection' },
      { label: '∅', value: '\\emptyset', title: 'Empty Set' },
      { label: '∖', value: '\\setminus', title: 'Set Difference' },
      { label: '⇒', value: '\\Rightarrow', title: 'Implies' },
      { label: '⇔', value: '\\Leftrightarrow', title: 'Equivalent / Iff' },
      { label: '→', value: '\\to', title: 'To / Approaches' },
      { label: '∴', value: '\\therefore', title: 'Therefore' },
      { label: '∵', value: '\\because', title: 'Because' },
      { label: '∧', value: '\\land', title: 'Logical And' },
      { label: '∨', value: '\\lor', title: 'Logical Or' },
      { label: '¬', value: '\\neg', title: 'Logical Not' },
    ],
    Format: [
      { label: 'text', value: '\\text{}', title: 'Plain Text' },
      { label: 'ℝ', value: '\\mathbb{}', title: 'Blackboard Bold (sets)' },
      { label: 'bold', value: '\\mathbf{}', title: 'Bold Font' },
      { label: 'cal', value: '\\mathcal{}', title: 'Calligraphic Font' },
      { label: 'frak', value: '\\mathfrak{}', title: 'Fraktur Font' },
      { label: 'sans', value: '\\mathsf{}', title: 'Sans-Serif Font' },
      { label: 'circled', value: '\\enclose{circle}{}', title: 'Encircled Element (e.g. ①, ⓧ)' },
      { label: 'space', value: '\\,', title: 'Thin Space' },
      { label: 'space+', value: '\\quad', title: 'Medium Space' },
      { label: 'space++', value: '\\qquad', title: 'Large Space' },
    ],
  };

  const openModal = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    
    if (!targetId) {
      alert('Please click into a text field first to specify where to insert the equation.');
      return;
    }

    let selection = '';
    try {
      const input = document.getElementById(targetId);
      if (input && typeof input.value === 'string') {
        // selectionStart/End can throw on certain input types in some browsers
        try {
          const start = input.selectionStart || 0;
          const end = input.selectionEnd || 0;
          selection = input.value.substring(start, end) || '';
        } catch (selErr) {
          console.warn('Could not get selection:', selErr);
          selection = '';
        }
      }
    } catch (err) {
      console.error('MathToolbar open error:', err);
    }

    const clean = stripWrappingMathDelimiter(selection || '');
    
    setTempText(clean || '');
    setShowModal(true);
  };

  const handleInsertToPopup = (value) => {
    const input = popupInputRef.current;
    if (!input) return;

    const start = input.selectionStart;
    const end = input.selectionEnd;
    const currentVal = input.value;

    const newVal = currentVal.substring(0, start) + value + currentVal.substring(end);
    let newCursorPos = start + value.length;

    const editSlot = value.indexOf('  ');
    const firstBrace = value.indexOf('{');
    const firstParen = value.indexOf('(');
    const firstBracket = value.indexOf('[');
    if (editSlot !== -1) newCursorPos = start + editSlot + 1;
    else if (firstBrace !== -1) newCursorPos = start + firstBrace + 1;
    else if (firstParen !== -1) newCursorPos = start + firstParen + 1;
    else if (firstBracket !== -1) newCursorPos = start + firstBracket + 1;

    setTempText(newVal);

    setTimeout(() => {
      input.selectionStart = input.selectionEnd = newCursorPos;
      input.focus();
    }, 0);
  };

  const findTargetIndex = (latex, clickedText, occurrenceIndex) => {
    const symbolMap = {
      'π': '\\pi',
      'α': '\\alpha',
      'β': '\\beta',
      'γ': '\\gamma',
      'δ': '\\delta',
      'ε': '\\epsilon',
      'ζ': '\\zeta',
      'η': '\\eta',
      'θ': '\\theta',
      'λ': '\\lambda',
      'μ': '\\mu',
      'ρ': '\\rho',
      'σ': '\\sigma',
      'τ': '\\tau',
      'φ': '\\phi',
      'ω': '\\omega',
      'Γ': '\\Gamma',
      'Δ': '\\Delta',
      'Θ': '\\Theta',
      'Λ': '\\Lambda',
      'Π': '\\Pi',
      'Σ': '\\Sigma',
      'Φ': '\\Phi',
      'Ψ': '\\Psi',
      'Ω': '\\Omega',
      '∫': '\\int',
      '∬': '\\iint',
      '∮': '\\oint',
      '±': '\\pm',
      '∓': '\\mp',
      '×': '\\times',
      '⋅': '\\cdot',
      '÷': '\\div',
      '≈': '\\approx',
      '≠': '\\neq',
      '≤': '\\le',
      '≥': '\\ge',
      '≡': '\\equiv',
      '∝': '\\propto',
      '∀': '\\forall',
      '∃': '\\exists',
      '∈': '\\in',
      '∉': '\\notin',
      '⊂': '\\subset',
      '∪': '\\cup',
      '∩': '\\cap',
      '∅': '\\emptyset',
      '∖': '\\setminus',
      '⇒': '\\Rightarrow',
      '⇔': '\\Leftrightarrow',
      '→': '\\to',
      '∴': '\\therefore',
      '∵': '\\because',
      '∧': '\\land',
      '∨': '\\lor',
      '¬': '\\neg',
    };

    const searchTerms = [];
    if (symbolMap[clickedText]) {
      searchTerms.push(symbolMap[clickedText]);
    }
    if (/^[a-zA-Z]+$/.test(clickedText)) {
      searchTerms.push('\\' + clickedText);
    }
    searchTerms.push(clickedText);

    // Sort searchTerms by length descending to match command first (e.g. \sin before sin)
    searchTerms.sort((a, b) => b.length - a.length);

    const matches = [];
    for (const term of searchTerms) {
      let pos = latex.indexOf(term);
      while (pos !== -1) {
        // Prevent matching a substring of an already matched term (e.g. 'sin' inside '\sin')
        const isOverlapping = matches.some(m => 
          (pos >= m.pos && pos < m.pos + m.len) || 
          (pos + term.length > m.pos && pos + term.length <= m.pos + m.len)
        );
        
        if (!isOverlapping) {
          matches.push({ pos, len: term.length });
        }
        pos = latex.indexOf(term, pos + 1);
      }
    }
    
    if (matches.length === 0) return null;
    matches.sort((a, b) => a.pos - b.pos);
    
    return matches[Math.min(occurrenceIndex, matches.length - 1)] || null;
  };

  const handlePreviewClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const targetElement = e.target.nodeType === Node.ELEMENT_NODE
      ? e.target
      : e.target.parentElement;
    const container = e.currentTarget;
    const leaf = targetElement?.closest(previewLeafSelector) || targetElement?.querySelector(previewLeafSelector);
    const structure = targetElement?.closest(previewStructureSelector);
    let match = getSourceMatchFromClass(targetElement);

    if (!match && structure && !leaf) {
      const command = structure.matches('mjx-frac') ? '\\frac' : '\\sqrt';
      const commandIndex = tempText.indexOf(command);
      match = commandIndex === -1 ? null : findCommandBlock(tempText, command, commandIndex);
    }

    if (!match && leaf) {
      const allLeaves = getPreviewLeaves(container);
      const renderedLeaves = allLeaves.filter(item => normalizePreviewText(item.textContent));
      const clickedIndex = renderedLeaves.indexOf(leaf);
      const sourceTokens = buildLatexPreviewTokens(tempText);
      const renderedTokens = getRenderedPreviewTokens(renderedLeaves);
      const sourceToken = findAlignedSourceToken(renderedTokens, sourceTokens, clickedIndex);
      const clickedText = normalizePreviewText(leaf.textContent);

      if (sourceToken && (!clickedText || sourceToken.display === clickedText)) {
        match = { pos: sourceToken.pos, len: sourceToken.len };
      }
    }

    if (!match && leaf) {
      const clickedText = normalizePreviewText(leaf.textContent);
      if (!clickedText) return;

      const allLeaves = getPreviewLeaves(container);
      let occurrenceIndex = 0;
      for (let i = 0; i < allLeaves.length; i++) {
        if (normalizePreviewText(allLeaves[i].textContent) === clickedText) {
          if (allLeaves[i] === leaf) {
            break;
          }
          occurrenceIndex++;
        }
      }

      match = findTargetIndex(tempText, clickedText, occurrenceIndex);
    }

    if (match && popupInputRef.current) {
      const textarea = popupInputRef.current;
      textarea.focus();
      textarea.setSelectionRange(match.pos, match.pos + match.len);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(match.pos, match.pos + match.len);
      }, 0);
    }
  };

  const handleInsertToMain = () => {
    try {
      const input = document.getElementById(targetId);
      if (!input) {
        setShowModal(false);
        return;
      }

      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const currentVal = input.value || '';

      let finalEq = (tempText || '').trim();
      if (finalEq) {
        if (!findWrappingMathDelimiter(finalEq)) {
          if (mathMode === 'display') {
            finalEq = `$$${finalEq}$$`;
          } else {
            finalEq = `$${finalEq}$`;
          }
        }
      }

      const newVal = currentVal.substring(0, start) + finalEq + currentVal.substring(end);

      // Safe way to trigger React state update
      const setter = input.tagName === 'TEXTAREA' 
        ? Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value")?.set
        : Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;

      if (setter) {
        setter.call(input, newVal);
      } else {
        input.value = newVal;
      }

      // Dispatch event to make sure React picks it up
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));

      // Move cursor to end of inserted text
      const newPos = start + finalEq.length;
      try {
        input.setSelectionRange(newPos, newPos);
      } catch (e) {}
      
      input.focus();
      setShowModal(false);
    } catch (err) {
      console.error('Final insert error:', err);
      setShowModal(false);
    }
  };

  return (
    <>
      <div style={{ display: 'inline-block' }}>
        <button
          type="button"
          onClick={openModal}
          title="Equation Builder"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--primary)',
            color: 'white',
            border: 'none',
            boxShadow: 'var(--shadow-sm)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
        >
          <i className="fas fa-calculator" style={{ fontSize: '1rem' }}></i>
        </button>
      </div>

      {showModal && (
        <div
          className="modal-overlay"
          style={{ zIndex: 10002 }}
          onClick={stopModalEventPropagation}
          onMouseDown={stopModalEventPropagation}
        >
          <div
            className="modal-card math-builder-card"
            onClick={stopModalEventPropagation}
            onMouseDown={stopModalEventPropagation}
          >
            <div className="modal-header">
              <h3>Equation Builder</h3>
              <button className="icon-button" onClick={() => setShowModal(false)}><i className="fas fa-times" /></button>
            </div>

            <div className="math-builder-body">
              <div className="math-categories-tabs">
                {Object.keys(categories).map(cat => (
                  <button
                    key={cat}
                    className={`math-tab-btn ${activeCategory === cat ? 'active' : ''}`}
                    onClick={() => setActiveCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="math-toolbar-full">
                {categories[activeCategory].map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    className="math-toolbar-btn"
                    onClick={() => handleInsertToPopup(s.value)}
                    title={s.title}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '20px', alignItems: 'center', margin: '4px 0 8px', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>Equation Mode:</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: 'var(--text-main)', cursor: 'pointer', fontWeight: 500 }}>
                  <input 
                    type="radio" 
                    name="mathMode" 
                    checked={mathMode === 'inline'} 
                    onChange={() => setMathMode('inline')} 
                    style={{ cursor: 'pointer', margin: 0 }}
                  />
                  Inline ($...$)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: 'var(--text-main)', cursor: 'pointer', fontWeight: 500 }}>
                  <input 
                    type="radio" 
                    name="mathMode" 
                    checked={mathMode === 'display'} 
                    onChange={() => setMathMode('display')} 
                    style={{ cursor: 'pointer', margin: 0 }}
                  />
                  Display Block ($$...$$)
                </label>
              </div>

              <div className="math-builder-split-container">
                <div className="math-builder-pane">
                  <label style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>LaTeX Code Editor</label>
                  <textarea
                    ref={popupInputRef}
                    value={tempText}
                    onChange={(e) => setTempText(e.target.value)}
                    placeholder="Type LaTeX here (e.g. \int_0^\pi \sin(x) dx)"
                    className="math-builder-input"
                  />
                </div>

                <div className="math-builder-pane">
                  <label style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>Live Mathematical Preview</label>
                  <div className="preview-container" onMouseDown={handlePreviewClick} style={{ cursor: 'pointer' }}>
                    <MathText text={previewMathText} />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="button" onClick={handleInsertToMain}>Insert Equation</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MathToolbar;
